import React, { useEffect, useRef, useState } from 'react';
import {
    ModalBody,
    SectionTitle,
    ItemRow,
    ItemDescription,
    ItemCost,
    Header,
    HeaderSubtitle,
    HeaderTitle,
    ModalContainer,
    Overlay,
    ContentWrapper,
    SectionBigTitle,
    CostWrapper,
    MinCostText,
    SectionSubtitle,
    TotalPercentage,
    PromotionBadge,
    PromotionText,
} from './pricing-modal.styles';
import MaterialIcon from '../../components/material-icon';
import { colorPalette } from '../../utils/theme';
import { useTranslation } from 'react-i18next';

interface PricingModalProps {
    isVisible: boolean;
    isPremium?: boolean;
    isResidencePromotion?: boolean;
    onClose: () => void;
}

const PricingModal: React.FC<PricingModalProps> = ({ isVisible, isPremium = false, isResidencePromotion = false, onClose }) => {
    const { t } = useTranslation();
    
    // States
    const [isClosing, setIsClosing] = useState(false);
    const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Functions
    const handleClose = () => {
        setIsClosing(true);
        closeTimeoutRef.current = setTimeout(() => {
            onClose();
            setIsClosing(false);
        }, 300);
    };

    useEffect(() => {
        return () => {
            if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
        };
    }, []);

    // Prevent scrolling down
    useEffect(() => {
        if (isVisible) {
            // Disable background scrolling
            document.body.style.overflow = 'hidden';

            // Prevent touchmove only if the event is outside the modal
            const preventBackgroundScroll = (e: TouchEvent) => {
                if (!(e.target as HTMLElement).closest('.sidebar-modal-content')) {
                    e.preventDefault();
                }
            };

            document.addEventListener('touchmove', preventBackgroundScroll, { passive: false });

            return () => {
                // Restore background scrolling
                document.body.style.overflow = 'unset';
                document.removeEventListener('touchmove', preventBackgroundScroll);
            };
        }
    }, [isVisible]);

    if (!isVisible && !isClosing) return null;

    return (
        <Overlay onClick={handleClose}>
            <ModalContainer onClick={(e) => e.stopPropagation()} isClosing={isClosing}>
                <Header>
                    <div>
                        <HeaderTitle>{t('pricing_modal.header.title')}</HeaderTitle>
                        <HeaderSubtitle>
                            {isResidencePromotion ? t('labels.premium_special') : isPremium ? t('labels.premium_package') : t('labels.basic_package')}
                        </HeaderSubtitle>
                    </div>
                    <MaterialIcon icon="close" size={24} color={colorPalette.blue500} onClick={handleClose} />
                </Header>
                <ModalBody className="sidebar-modal-content">
                    <ContentWrapper>
                        <SectionTitle>{t('pricing_modal.pre_handover.title')}</SectionTitle>
                        <ItemDescription>{t('pricing_modal.pre_handover.fees_description')}</ItemDescription>
                        <p>
                            {t('pricing_modal.pre_handover.admin_description')}
                        </p>
                        <ItemRow>
                            <div>
                                <span>{t('pricing_modal.pre_handover.notary_fees.title')}</span>
                                <p style={{ marginBottom: 0 }}>{t('pricing_modal.pre_handover.notary_fees.description')}</p>
                            </div>
                            <ItemCost>AED 1.500</ItemCost>
                        </ItemRow>
                        <ItemRow>
                            <span>{t('pricing_modal.pre_handover.handover_coordination')}</span>
                            <ItemCost>AED 1.000¹</ItemCost>
                        </ItemRow>
                        <ItemRow>
                            <span>{t('pricing_modal.pre_handover.legal_representation')}</span>
                            <ItemCost>AED 3.500¹</ItemCost>
                        </ItemRow>
                        <ItemRow>
                            <span>{t('pricing_modal.pre_handover.snagging_de_snagging')}</span>
                            <ItemCost>AED 1.8/sqft¹</ItemCost>
                        </ItemRow>
                        <ItemRow>
                            <span>{t('pricing_modal.pre_handover.deep_cleaning')}</span>
                            <ItemCost>AED 0.6/sqft¹</ItemCost>
                        </ItemRow>
                        <SectionSubtitle style={{ marginTop: 24 }}>{t('pricing_modal.pre_handover.utility_deposits_title')}</SectionSubtitle>
                        <p>
                            {t('pricing_modal.pre_handover.utility_deposits_description')}
                        </p>
                        <ItemRow>
                            <span>{t('pricing_modal.pre_handover.dewa_deposit')}</span>
                            <ItemCost>AED 2.155</ItemCost>
                        </ItemRow>
                        <ItemRow>
                            <span>{t('pricing_modal.pre_handover.empower_deposit')}</span>
                            <ItemCost>AED 2.000</ItemCost>
                        </ItemRow>
                            <SectionBigTitle style={{ marginTop: 24 }}>{t('pricing_modal.management_costs.title')}</SectionBigTitle>
                            <ItemRow>
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                    <span>{t('pricing_modal.management_costs.management_fee')} ({isPremium ? t('labels.premium_package').replace(' Package', '').replace('-Paket', '') : t('labels.basic_package').replace(' Package', '').replace('-Paket', '')})</span>
                                    {isResidencePromotion && (
                                        <PromotionBadge>
                                            <PromotionText>{t('pricing_modal.management_costs.promotion_text')}</PromotionText>
                                        </PromotionBadge>
                                    )}
                                </div>
                                <CostWrapper>
                                    <TotalPercentage>{isPremium ? t('pricing_modal.management_costs.percentage_premium') : t('pricing_modal.management_costs.percentage_basic')} {t('pricing_modal.management_costs.annual_rent')}</TotalPercentage>
                                    <MinCostText>{t('pricing_modal.management_costs.minimum_fee')}</MinCostText>
                                </CostWrapper>
                            </ItemRow>
                                                        <p style={{ marginTop: 16 }}>
                                {t('pricing_modal.footnotes.vat_note')}<br></br>
                                {isResidencePromotion && (
                                    <>{t('pricing_modal.footnotes.promotion_note')}<br></br></>
                                )}
                                <br></br>
                                {t('pricing_modal.footnotes.general_terms')} 
                                <br></br><br></br>{t('pricing_modal.footnotes.handover_note')}
                            </p>
                    </ContentWrapper>
                </ModalBody>
            </ModalContainer>
        </Overlay>
    );
};

export default PricingModal;